import styled from "@emotion/styled";
import type { StarAtlasOpenOrder } from "@stepfinance/dex";
import type { StepColumnsType } from "@stepfinance/react";
import {
  formatBalance,
  greenTag,
  makeSimpleFilters,
  MultiIcon,
  redTag,
  StepAssetBreakdownPopoverChip,
  StepPopoverCellWrapper,
  StepTable,
  TagInfo,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useEnrichedWallet } from "../../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../../contexts/language/language";
import { getTokenByMint } from "../../../../utils/tokenInfo";
import { isPaginationHidden, useLocalStorageState } from "../../../../utils/utils";
import RedirectLink from "../../../RedirectLink/RedirectLink";
import { PAGE_SIZE_OPTIONS, StepPopoverAssetWithIconRow } from "../../../shared";
import CellValue from "../../../Table/CellValue";
import { ChevronCollapse } from "../../ChevronCollapse/ChevronCollapse";
import {
  CancelAllContainer,
  CancelContainer,
  CloseColumnContainer,
  ValueContainer,
  ValueText,
  ValueTitle,
} from "../Openbook";
import { CloseAllSAOrdersButton } from "./CloseAllButton";
import { CloseSAOrderButton } from "./CloseButton";

type StarAtlasOpenOrdersTableProps = {
  openOrders: StarAtlasOpenOrder[];
};

export const StarAtlasOpenOrdersTable: FC<StarAtlasOpenOrdersTableProps> = ({ openOrders }) => {
  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        dashboard: {
          modules: { DEXModule },
        },
      },
    },
  } = useLanguageContext();

  const { watching } = useEnrichedWallet();
  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "starAtlasOpenOrdersPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const totalValueUSD = useMemo(() => openOrders.reduce<number>((a, c) => a + (c.valueInUSD ?? 0), 0), [openOrders]);

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(openOrders.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [openOrders.length, paginationSetting]);

  const columns: StepColumnsType<StarAtlasOpenOrder> = [
    {
      title: common.market,
      dataIndex: "itemName",
      key: "itemName",
      className: "column-string",
      sorter: (a, b) => a.itemName.localeCompare(b.itemName),
      render: (_, { itemName, quoteMint, logoURI }) => {
        const formatItemName = (name: string) => {
          const upperCaseName = name.toUpperCase();
          return upperCaseName.length > 20 ? `${upperCaseName.slice(0, 20)}...` : upperCaseName;
        };
        const currencyInfo = getTokenByMint(quoteMint);
        return (
          <MarketCell>
            <MultiIcon
              iconList={[
                {
                  src: logoURI,
                },
                { src: currencyInfo?.logoURI },
              ]}
            />
            <RedirectLink href="https://play.staratlas.com/market/my-orders">
              {formatItemName(itemName) + "/" + currencyInfo?.symbol}
            </RedirectLink>
          </MarketCell>
        );
      },
    },
    {
      title: common.type,
      dataIndex: "itemType",
      key: "itemType",
      className: "column-string",
      render: (_, { itemType }) => (
        <TagInfo useSpaceMono={false} tagType="text">
          {itemType}
        </TagInfo>
      ),
      filters: makeSimpleFilters(openOrders.map((order) => order.itemType)),
    },
    {
      title: common.side,
      dataIndex: "side",
      key: "side",
      className: "column-string",
      render: (_, { side }) => {
        const tagColor = side === "buy" ? greenTag : redTag;
        return (
          <TagInfo useSpaceMono={false} tagType="text" tagColorOverride={tagColor}>
            {side.toUpperCase()}
          </TagInfo>
        );
      },
      filters: makeSimpleFilters(openOrders.map((order) => order.side)),
    },
    {
      title: common.amount,
      dataIndex: "size",
      key: "size",
      className: "column-number",
      sorter: (a, b) => b.size - a.size,
      render: (_, { size }) => <CellValue cellType="number">{size}</CellValue>,
    },
    {
      title: headers.price,
      dataIndex: "price",
      key: "price",
      className: "column-number",
      sorter: (a, b) => b.quoteAmount - a.quoteAmount,
      render: (_, { priceInUSD, quoteAmount, quoteMint }) => {
        const currencyTokenInfo = getTokenByMint(quoteMint);
        const priceComponent = (
          <CellValue cellType="value" fontWeight="bold">
            {priceInUSD !== null ? formatBalance(priceInUSD, 6) : null}
          </CellValue>
        );
        const content = (
          <StepPopoverAssetWithIconRow
            amount={formatBalance(quoteAmount, currencyTokenInfo?.decimals)}
            symbol={currencyTokenInfo?.symbol ?? ""}
            tokenIconMintAddress={quoteMint}
          />
        );

        return currencyTokenInfo?.symbol !== "USDC" ? (
          <StepPopoverCellWrapper>
            <StepAssetBreakdownPopoverChip
              content={content}
              title={headers.price}
              assetValue={priceInUSD ?? 0}
              assetValueDisplay={priceComponent}
            />
          </StepPopoverCellWrapper>
        ) : (
          priceComponent
        );
      },
    },
    {
      title: () => (
        <CloseColumnContainer>
          {!watching && (
            <CancelAllContainer>
              <CloseAllSAOrdersButton orderIds={openOrders.map((order) => order.orderAddress)} />
            </CancelAllContainer>
          )}
          <ValueTitle>{common.value}</ValueTitle>
        </CloseColumnContainer>
      ),
      dataIndex: "value",
      key: "value",
      className: "column-number",
      sorter: (a, b) => (b.valueInUSD ?? 0) - (a.valueInUSD ?? 0),
      defaultSortOrder: "descend",
      render: (_, { valueInUSD, quoteMint, amount, orderAddress }) => {
        const currencyTokenInfo = getTokenByMint(quoteMint);
        const content = (
          <StepPopoverAssetWithIconRow
            key={quoteMint}
            tokenIconMintAddress={quoteMint}
            amount={formatBalance(amount, currencyTokenInfo?.decimals)}
            symbol={currencyTokenInfo?.symbol ?? ""}
          />
        );

        const valueComponent = (
          <ValueText>
            <CellValue cellType="value" fontWeight="bold">
              {valueInUSD !== null ? formatBalance(valueInUSD, 6) : null}
            </CellValue>
          </ValueText>
        );
        return (
          <ValueContainer>
            {!watching && (
              <CancelContainer>
                <CloseSAOrderButton orderId={orderAddress} />
              </CancelContainer>
            )}
            {currencyTokenInfo?.symbol !== "USDC" ? (
              <StepPopoverCellWrapper>
                <StepAssetBreakdownPopoverChip
                  title={common.value}
                  content={content}
                  assetValue={amount}
                  assetValueDisplay={valueComponent}
                />
              </StepPopoverCellWrapper>
            ) : (
              valueComponent
            )}
          </ValueContainer>
        );
      },
    },
  ];

  return (
    <StarAtlasOrdersContainer>
      <ChevronCollapse title={DEXModule.starAtlasOpenOrders} value={totalValueUSD} isCollapseChild>
        <StepTable
          rowKey={(record, index) => `${index}-${record.symbol}-${record.orderAddress}`}
          onChange={(pagination) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
          }}
          pagination={pagination}
          columns={columns}
          dataSource={openOrders}
        />
      </ChevronCollapse>
    </StarAtlasOrdersContainer>
  );
};

const MarketCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StarAtlasOrdersContainer = styled.div`
  margin-top: 20px;
`;
